import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';

const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = (location.state as { email?: string })?.email || '';

  const [resending, setResending] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);

  // Countdown timer for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleResendEmail = async () => {
    if (!email) {
      setError('Email address not found. Please sign up again.');
      return;
    }

    if (countdown > 0) {
      return; // Prevent spam clicking
    }

    setResending(true);
    setError('');
    setMessage('');

    try {
      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });

      if (resendError) {
        setError(resendError.message || 'Failed to resend verification email');
      } else {
        setMessage('Verification email sent! Please check your inbox and spam folder.');
        setCountdown(60); // 60 second cooldown
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          {/* Email Icon */}
          <div className="mx-auto w-16 h-16 bg-[#E8F5F2] rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-[#3A5E63]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>

          <h2 className="text-3xl font-bold text-[#3A5E63] font-serif">Check Your Email</h2>
          <p className="mt-3 text-sm text-[#344854]/70 leading-relaxed">
            We've sent a verification link to
          </p>
          {email && (
            <p className="mt-1 text-base font-medium text-[#3A5E63]">
              {email}
            </p>
          )}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
            {error}
          </div>
        )}

        {message && (
          <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-md text-sm">
            {message}
          </div>
        )}

        <div className="bg-[#F8F6F2] rounded-lg p-6 mb-6">
          <h3 className="text-sm font-semibold text-[#344854] mb-3">Next Steps:</h3>
          <ol className="space-y-2 text-sm text-[#344854]/80">
            <li className="flex items-start">
              <span className="font-semibold mr-2">1.</span>
              <span>Open your email inbox</span>
            </li>
            <li className="flex items-start">
              <span className="font-semibold mr-2">2.</span>
              <span>Click the verification link in the email from Healing Lotus Spa</span>
            </li>
            <li className="flex items-start">
              <span className="font-semibold mr-2">3.</span>
              <span>You'll be redirected to log in</span>
            </li>
          </ol>
        </div>

        <div className="space-y-3">
          <p className="text-sm text-[#344854]/70 text-center">
            Didn't receive the email?
          </p>

          <button
            onClick={handleResendEmail}
            disabled={resending || countdown > 0}
            className="w-full flex justify-center items-center py-2 px-4 border border-[#3A5E63] rounded-md shadow-sm text-sm font-medium text-[#3A5E63] bg-white hover:bg-[#F8F6F2] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3A5E63] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {resending ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </>
            ) : countdown > 0 ? (
              `Resend in ${countdown}s`
            ) : (
              'Resend Verification Email'
            )}
          </button>

          <div className="text-center pt-4 border-t border-[#B2D8C5]">
            <button
              onClick={() => navigate('/login')}
              className="text-sm text-[#3A5E63] hover:text-[#2d4a4d] font-medium transition-colors"
            >
              Already verified? Log in
            </button>
          </div>
        </div>

        <div className="mt-6 p-4 bg-blue-50 rounded-md">
          <p className="text-xs text-[#344854]/70">
            <strong>Note:</strong> The verification link expires in 24 hours. If you don't see the email, check your spam or junk folder.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
